import { ArrowRight, Play } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Hero = () => {
  const scrollTo = (hash) => {
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden pt-28 pb-20"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1770992161088-7ad66282c9af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBvZmZpY2UlMjBpbnRlcmlvciUyMGRhcmslMjBncmVlbnxlbnwwfHx8fDE3Nzc5MTUzOTN8MA&ixlib=rb-4.1.0&q=85"
          alt="Luxury office"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#121A12]/92 via-[#121A12]/88 to-[#121A12]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(227,162,26,0.12),transparent_50%)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 md:px-12 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 reveal">
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E3A21A]/25 bg-[#1E2A1E]/60 backdrop-blur-sm mb-8"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E3A21A] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E3A21A]"></span>
              </span>
              <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E3A21A]">
                Performance Marketing · India
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight text-[#F5F5F0] font-medium"
            >
              We don't do
              <br />
              marketing.
              <br />
              <span className="text-[#E3A21A] italic font-light">We drive</span>{" "}
              <span className="text-[#E3A21A]">revenue.</span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-8 max-w-2xl text-base sm:text-lg text-[#A8B8A8] leading-relaxed font-body"
            >
              We help <span className="text-[#F5F5F0] font-semibold">real estate</span>,{" "}
              <span className="text-[#F5F5F0] font-semibold">lifestyle</span>, and{" "}
              <span className="text-[#F5F5F0] font-semibold">education</span> brands
              scale with performance marketing and high-converting funnels — built
              around ROI, not vanity metrics.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Button
                data-testid="hero-cta-book-call"
                onClick={() => scrollTo("#contact")}
                className="group h-14 px-7 rounded-full bg-[#E3A21A] text-[#121A12] font-semibold text-base hover:bg-[#F0B63A] hover:-translate-y-0.5 transition-all shadow-[0_0_40px_rgba(227,162,26,0.25)]"
              >
                Book Free Strategy Call
                <ArrowRight className="ml-1 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>

              <Button
                data-testid="hero-cta-case-studies"
                onClick={() => scrollTo("#case-studies")}
                variant="outline"
                className="h-14 px-7 rounded-full bg-transparent border-[#E3A21A]/25 text-[#F5F5F0] hover:bg-[#E3A21A]/5 hover:border-[#E3A21A] hover:text-[#F5F5F0] font-medium text-base"
              >
                <Play className="mr-1 h-4 w-4 text-[#E3A21A]" />
                View Case Studies
              </Button>
            </div>
          </div>

          {/* Right-side premium metric block */}
          <div className="lg:col-span-4 reveal" style={{ animationDelay: "0.2s" }}>
            <div className="relative rounded-2xl border border-[#E3A21A]/15 bg-[#1E2A1E]/70 backdrop-blur-xl p-8 overflow-hidden">
              <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-[#E3A21A]/15 blur-3xl" />
              <div className="text-xs tracking-[0.25em] uppercase text-[#E3A21A] font-bold mb-6">
                Aggregate Results
              </div>
              <div className="space-y-6">
                <div>
                  <div className="font-display text-5xl font-medium text-[#F5F5F0] tracking-tight">
                    ₹<span className="text-[#E3A21A]">5Cr+</span>
                  </div>
                  <div className="text-sm text-[#A8B8A8] mt-1">
                    revenue generated for clients
                  </div>
                </div>
                <div className="h-px bg-[#E3A21A]/10" />
                <div>
                  <div className="font-display text-5xl font-medium text-[#F5F5F0] tracking-tight">
                    <span className="text-[#E3A21A]">100+</span>
                  </div>
                  <div className="text-sm text-[#A8B8A8] mt-1">
                    campaigns managed end-to-end
                  </div>
                </div>
                <div className="h-px bg-[#E3A21A]/10" />
                <div>
                  <div className="font-display text-5xl font-medium text-[#F5F5F0] tracking-tight">
                    <span className="text-[#E3A21A]">3</span> niches
                  </div>
                  <div className="text-sm text-[#A8B8A8] mt-1">
                    real estate · lifestyle · education
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#121A12] to-transparent pointer-events-none" />
    </section>
  );
};
